import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Download,
  FileText,
  Globe,
  Loader2,
  LayoutTemplate,
  PenSquare,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  AlertCircle,
  Upload,
  User,
  CheckCircle
} from 'lucide-react'
import ResumeEditor from '@/components/resume/ResumeEditor'
import ResumePreview from '@/components/resume/ResumePreview'
import TemplateSelector from '@/components/resume/TemplateSelector'
import TemplateGallery from '@/components/resume/TemplateGallery'
import { TEMPLATE_REGISTRY } from '@/components/resume/templates/template-registry'
import { languages, translateResumeData } from '@/lib/translations'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

type EditorView = 'profile' | 'content' | 'design'

export default function ResumeBuilderPage() {
  const navigate = useNavigate()
  const [editorView, setEditorView] = useState<EditorView>('profile')
  const [activeTab, setActiveTab] = useState('personal')
  const [selectedTemplate, setSelectedTemplate] = useState('modern')
  const [showPreview, setShowPreview] = useState(true)
  const [selectedLanguage, setSelectedLanguage] = useState('en')
  const [isTranslating, setIsTranslating] = useState(false)
  const [translatedData, setTranslatedData] = useState<any>(null)
  const [showTemplateGallery, setShowTemplateGallery] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(0.8)
  const [completionScore, setCompletionScore] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState('')
  const [selectedColor, setSelectedColor] = useState('blue')
  const originalDataRef = useRef<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [resumeData, setResumeData] = useState({
    personalInfo: {
      fullName: 'JOHN SMITH',
      email: 'john.smith@email.com',
      phone: '+1 (555) 123-4567',
      address: 'New York, NY 10001',
      linkedin: 'linkedin.com/in/johnsmith',
      website: 'johnsmith.dev',
      github: 'github.com/johnsmith',
      photo: ''
    },
    summary: 'Results-driven Software Engineer with 5+ years of experience in full-stack development, cloud architecture, and team leadership. Proven track record of delivering scalable solutions that improve system performance by 40% and reduce operational costs. Passionate about modern technologies and agile methodologies.',
    experience: [
      {
        id: '1',
        jobTitle: 'Senior Software Engineer',
        company: 'Tech Innovations Inc.',
        location: 'New York, NY',
        startDate: 'January 2022',
        endDate: '',
        description: '• Led development of microservices architecture serving 1M+ daily active users, improving system scalability by 60%\n• Mentored team of 4 junior developers and conducted comprehensive code reviews to maintain 95% code quality standards\n• Implemented CI/CD pipelines using Jenkins and Docker, reducing deployment time from 2 hours to 15 minutes\n• Collaborated with product managers and UX designers to deliver 12 major features ahead of schedule'
      },
      {
        id: '2',
        jobTitle: 'Full Stack Developer',
        company: 'Digital Solutions LLC',
        location: 'Boston, MA',
        startDate: 'June 2020',
        endDate: 'December 2021',
        description: '• Developed responsive web applications using React, Node.js, and PostgreSQL for 50+ enterprise clients\n• Optimized database queries and API performance, resulting in 40% improvement in application load times\n• Integrated payment gateways and third-party APIs for e-commerce platforms processing $2M+ monthly transactions\n• Participated in agile development processes and daily standups as part of 8-person development team'
      }
    ],
    education: [
      {
        id: '1',
        degree: 'Bachelor of Science in Computer Science',
        institution: 'Massachusetts Institute of Technology',
        location: 'Cambridge, MA',
        graduationDate: 'May 2020',
        gpa: '3.8'
      }
    ],
    skills: [
      {
        category: 'Programming Languages',
        skills: ['JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'SQL']
      },
      {
        category: 'Frontend Technologies',
        skills: ['React', 'Vue.js', 'Angular', 'HTML5', 'CSS3', 'Tailwind CSS']
      },
      {
        category: 'Backend Technologies',
        skills: ['Node.js', 'Express.js', 'Django', 'Spring Boot', 'REST APIs', 'GraphQL']
      },
      {
        category: 'Databases & Cloud',
        skills: ['PostgreSQL', 'MongoDB', 'Redis', 'AWS', 'Docker', 'Kubernetes']
      }
    ],
    projects: [
      {
        id: '1',
        name: 'E-Commerce Platform',
        description: 'Full-stack application with real-time inventory management and payment processing serving 10,000+ monthly users',
        technologies: ['React', 'Node.js', 'PostgreSQL', 'Stripe API']
      },
      {
        id: '2',
        name: 'Real-Time Chat Application',
        description: 'Scalable messaging platform with WebSocket support, supporting 5,000+ concurrent connections',
        technologies: ['Socket.io', 'Redis', 'MongoDB', 'React']
      }
    ],
    certifications: [
      {
        id: '1',
        name: 'AWS Certified Solutions Architect',
        issuer: 'Amazon Web Services',
        date: 'March 2023'
      }
    ]
  })

  // Store original data on first mount
  useEffect(() => {
    if (!originalDataRef.current) {
      originalDataRef.current = resumeData
    }
  }, [])

  // Load data from sessionStorage (from career profile)
  useEffect(() => {
    const loadDataFromCareerProfile = () => {
      const storedData = sessionStorage.getItem('resumeBuilderData')
      if (!storedData) return

      try {
        const profileData = JSON.parse(storedData)
        console.log('📥 Loading profile data from career profile:', profileData)

        const newResumeData: any = { ...resumeData }

        // Map Basic Info
        if (profileData.basic) {
          const basic = profileData.basic
          newResumeData.personalInfo = {
            fullName: basic.full_name || `${basic.first_name || ''} ${basic.last_name || ''}`.trim() || resumeData.personalInfo.fullName,
            email: basic.email || resumeData.personalInfo.email,
            phone: basic.phone || resumeData.personalInfo.phone,
            address: basic.current_location || resumeData.personalInfo.address,
            linkedin: basic.linkedin_url || resumeData.personalInfo.linkedin,
            website: basic.github_url || resumeData.personalInfo.website,
            github: basic.github_url || resumeData.personalInfo.github,
            photo: resumeData.personalInfo.photo
          }

          // Map Summary
          if (basic.summary) {
            newResumeData.summary = basic.summary
          }
        }

        // Map Education
        if (profileData.education?.education && Array.isArray(profileData.education.education)) {
          newResumeData.education = profileData.education.education.map((edu: any, index: number) => ({
            id: `edu-${index + 1}`,
            degree: edu.degree || '',
            institution: edu.institution || '',
            location: edu.location || '',
            graduationDate: edu.end_date || edu.graduationDate || '',
            gpa: edu.grade || ''
          }))
        }

        // Map Work Experience
        if (profileData.workExperience?.work_experience && Array.isArray(profileData.workExperience.work_experience)) {
          newResumeData.experience = profileData.workExperience.work_experience.map((exp: any, index: number) => ({
            id: `exp-${index + 1}`,
            jobTitle: exp.title || '',
            company: exp.company || '',
            location: exp.location || '',
            startDate: exp.start_date || '',
            endDate: exp.is_current ? 'Present' : (exp.end_date || ''),
            description: exp.description || ''
          }))
        }

        // Map Skills
        if (profileData.skills?.skills && Array.isArray(profileData.skills.skills)) {
          // Group skills by category
          const skillsByCategory = profileData.skills.skills.reduce((acc: any, skill: any) => {
            const category = skill.skillCategory || 'Technical Skills'
            if (!acc[category]) {
              acc[category] = []
            }
            acc[category].push(skill.skillName)
            return acc
          }, {})

          // Convert to array format expected by resume builder
          newResumeData.skills = Object.entries(skillsByCategory).map(([category, skills]: [string, any]) => ({
            category,
            skills
          }))
        }

        // Map Certifications
        if (profileData.certifications?.certifications && Array.isArray(profileData.certifications.certifications)) {
          newResumeData.certifications = profileData.certifications.certifications.map((cert: any, index: number) => ({
            id: `cert-${index + 1}`,
            name: cert.certification_name || '',
            issuer: cert.issuing_organization || '',
            date: cert.issue_date || ''
          }))
        }

        // Update resume data
        setResumeData(newResumeData)
        console.log('✅ Successfully loaded profile data into resume builder')

        // Clear sessionStorage after loading
        sessionStorage.removeItem('resumeBuilderData')

        // Switch to content tab to show the loaded data
        setEditorView('content')
      } catch (error) {
        console.error('❌ Error loading profile data:', error)
      }
    }

    loadDataFromCareerProfile()
  }, [])

  // Handle language change and translation
  useEffect(() => {
    const handleTranslation = async () => {
      if (selectedLanguage === 'en') {
        setTranslatedData(null)
        return
      }

      setIsTranslating(true)
      try {
        const sourceData = originalDataRef.current || resumeData
        const translated = await translateResumeData(sourceData, selectedLanguage)
        setTranslatedData(translated)
      } catch (error) {
        console.error('Translation failed:', error)
      } finally {
        setIsTranslating(false)
      }
    }

    handleTranslation()
  }, [selectedLanguage])

  // Update original data ref when resume data changes
  useEffect(() => {
    if (selectedLanguage === 'en') {
      originalDataRef.current = resumeData
    }
  }, [resumeData, selectedLanguage])

  // Calculate completion score
  useEffect(() => {
    const score = calculateCompletionScore(resumeData)
    setCompletionScore(score)
  }, [resumeData])

  const calculateCompletionScore = (data: any): number => {
    let score = 0
    const maxScore = 100

    // Personal Info (25 points)
    if (data.personalInfo.fullName) score += 5
    if (data.personalInfo.email) score += 5
    if (data.personalInfo.phone) score += 5
    if (data.personalInfo.address) score += 5
    if (data.personalInfo.linkedin || data.personalInfo.website) score += 5

    // Summary (15 points)
    if (data.summary && data.summary.length > 50) score += 15

    // Experience (25 points)
    if (data.experience && data.experience.length > 0) {
      score += 10
      if (data.experience.length >= 2) score += 10
      if (data.experience.some((exp: any) => exp.description && exp.description.length > 100)) score += 5
    }

    // Education (15 points)
    if (data.education && data.education.length > 0) score += 15

    // Skills (10 points)
    if (data.skills && data.skills.length > 0) score += 10

    // Projects (10 points)
    if (data.projects && data.projects.length > 0) score += 10

    return Math.min(score, maxScore)
  }

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 1.5))
  }

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.3))
  }

  const handleZoomReset = () => {
    setZoomLevel(0.8)
  }

  const handleResumeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['.pdf', '.docx', '.doc', '.txt']
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

    if (!allowedTypes.includes(fileExtension)) {
      setUploadMessage('Please upload a PDF, DOCX, DOC, or TXT file')
      return
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setUploadMessage('File size too large. Maximum allowed size is 10MB')
      return
    }

    setIsUploading(true)
    setUploadMessage('Extracting resume data...')

    try {
      // Import apiClient
      const { apiClient } = await import('@/lib/api')

      // Call the actual API with persist=false (preview only, no database save)
      const response = await apiClient.uploadResumeForProfile(file, false)

      if (response.success && response.data) {
        const extractedData = response.data
        console.log('✅ Resume extraction successful:', extractedData)

        setUploadMessage('Resume data extracted successfully!')

        // Map extracted data to resume format
        const newResumeData: any = { ...resumeData }

        // Basic Info
        if (extractedData.basic_info) {
          newResumeData.personalInfo = {
            fullName: extractedData.basic_info.full_name || resumeData.personalInfo.fullName,
            email: extractedData.basic_info.email || resumeData.personalInfo.email,
            phone: extractedData.basic_info.phone || resumeData.personalInfo.phone,
            address: extractedData.basic_info.address || resumeData.personalInfo.address,
            linkedin: extractedData.basic_info.linkedin_url || resumeData.personalInfo.linkedin,
            website: extractedData.basic_info.github_url || resumeData.personalInfo.website,
            github: extractedData.basic_info.github_url || resumeData.personalInfo.github,
            photo: resumeData.personalInfo.photo
          }
        }

        // Professional Summary
        if (extractedData.professional_summary) {
          newResumeData.summary = extractedData.professional_summary
        }

        // Work Experience
        if (extractedData.work_experience && extractedData.work_experience.length > 0) {
          newResumeData.experience = extractedData.work_experience.map((exp: any, index: number) => ({
            id: `exp-${index + 1}`,
            jobTitle: exp.title || '',
            company: exp.company || '',
            location: exp.location || '',
            startDate: exp.start_date || exp.dates?.split('-')[0]?.trim() || '',
            endDate: exp.end_date || (exp.is_current ? 'Present' : exp.dates?.split('-')[1]?.trim() || ''),
            description: exp.description || ''
          }))
        }

        // Education
        if (extractedData.education && extractedData.education.length > 0) {
          newResumeData.education = extractedData.education.map((edu: any, index: number) => ({
            id: `edu-${index + 1}`,
            degree: edu.degree || edu.degree_title || '',
            institution: edu.institution || edu.school || '',
            location: edu.location || '',
            graduationDate: edu.year || edu.graduationYear || edu.end_date || '',
            gpa: edu.grade || ''
          }))
        }

        // Skills
        if (extractedData.skills && extractedData.skills.length > 0) {
          // Group skills into categories if they're flat
          const skillsArray = Array.isArray(extractedData.skills) ? extractedData.skills : []

          // If skills are strings, create a single category
          if (skillsArray.length > 0 && typeof skillsArray[0] === 'string') {
            newResumeData.skills = [{
              id: 'skills-1',
              category: 'Technical Skills',
              skills: skillsArray
            }]
          } else {
            // Keep existing structure if already categorized
            newResumeData.skills = skillsArray
          }
        }

        // Projects (if available)
        if (extractedData.projects && extractedData.projects.length > 0) {
          newResumeData.projects = extractedData.projects.map((proj: any, index: number) => ({
            id: `proj-${index + 1}`,
            name: proj.name || proj.title || '',
            description: proj.description || '',
            technologies: proj.technologies || []
          }))
        }

        // Certifications
        if (extractedData.certifications && extractedData.certifications.length > 0) {
          newResumeData.certifications = extractedData.certifications.map((cert: any, index: number) => ({
            id: `cert-${index + 1}`,
            name: typeof cert === 'string' ? cert : cert.name || cert.title || '',
            issuer: typeof cert === 'object' ? (cert.organization || cert.issuer || '') : '',
            date: typeof cert === 'object' ? cert.date : ''
          }))
        }

        // Update resume data
        setResumeData(newResumeData)

        // Switch to content tab after successful upload
        setTimeout(() => {
          setEditorView('content')
        }, 1500)

      } else {
        setUploadMessage(response.error || 'Failed to extract resume data')
      }

    } catch (error) {
      console.error('Resume upload error:', error)
      setUploadMessage('An error occurred while uploading your resume')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleBack = () => {
    navigate('/candidate/career-profile')
  }

  const handleDownload = async () => {
    const resumeElement = document.getElementById('resume-preview')
    if (!resumeElement) {
      alert('Resume preview not found. Please try again.')
      return
    }

    try {
      // Clone the element to avoid affecting the visible preview
      const clonedElement = resumeElement.cloneNode(true) as HTMLElement

      // Create a temporary container with A4 dimensions
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '0'
      tempContainer.style.width = '793.7px' // A4 width at 96 DPI (210mm)
      tempContainer.style.minHeight = '1122.5px' // A4 height at 96 DPI (297mm)
      tempContainer.style.backgroundColor = '#ffffff'
      tempContainer.appendChild(clonedElement)
      document.body.appendChild(tempContainer)

      // Set the cloned element to A4 dimensions
      clonedElement.style.width = '793.7px'
      clonedElement.style.minHeight = '1122.5px'
      clonedElement.style.transform = 'none'
      clonedElement.style.margin = '0'

      // Wait for layout to settle
      await new Promise(resolve => setTimeout(resolve, 300))

      // Capture the A4-sized resume with optimized settings
      const canvas = await html2canvas(clonedElement, {
        scale: 1, // Reduced from 2 to normal scale (50% smaller)
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 793.7,
        height: clonedElement.scrollHeight
      })

      // Remove temporary container
      document.body.removeChild(tempContainer)

      // Create PDF - A4 size is 210mm x 297mm
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true // Enable PDF compression
      })

      const pdfWidth = 210 // A4 width in mm
      const pdfHeight = 297 // A4 height in mm
      const canvasWidth = canvas.width
      const canvasHeight = canvas.height

      // Convert pixel dimensions to mm (at 96 DPI: 1 inch = 96 pixels = 25.4mm)
      const pixelsPerMm = 96 / 25.4
      const imgWidth = pdfWidth // Fill the full page width
      const imgHeight = (canvasHeight / pixelsPerMm) // Scale height proportionally

      let heightLeft = imgHeight
      let position = 0

      // Use JPEG instead of PNG with compression for smaller file size
      const imgData = canvas.toDataURL('image/jpeg', 0.85) // 85% quality

      // Add first page (full width, no margins)
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight)
      heightLeft -= pdfHeight

      // Add additional pages if content is longer (full width, no margins)
      while (heightLeft > 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight)
        heightLeft -= pdfHeight
      }

      // Download with custom filename
      const fileName = `${resumeData.personalInfo.fullName || 'Resume'}_${selectedTemplate}.pdf`
      pdf.save(fileName.replace(/\s+/g, '_'))
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again.')
    }
  }

  // Use translated data for preview if available, otherwise use original
  const displayData = translatedData || resumeData

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Left Sidebar - Control Panel */}
      <aside className="w-[480px] bg-white/80 backdrop-blur-xl border-r border-white/20 flex flex-col shadow-2xl shadow-blue-500/10">
        {/* Header */}
        <div className="p-8 border-b border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Resume Builder
              </h1>
              <p className="text-sm text-gray-500 mt-1">Create your perfect resume</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700">Completion Progress</span>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${completionScore >= 80 ? 'bg-green-400' : completionScore >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`}></div>
                <span className="text-sm font-bold text-gray-800">{completionScore}%</span>
              </div>
            </div>
            <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out ${
                  completionScore >= 80 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                  completionScore >= 60 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 
                  'bg-gradient-to-r from-red-400 to-pink-500'
                } shadow-lg`}
                style={{ width: `${completionScore}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2 font-medium">
              {completionScore >= 80 ? '✨ Looking fantastic!' :
               completionScore >= 60 ? '🚀 Almost perfect!' :
               '� Keep building!'}
            </p>
          </div>
        </div>

        {/* Modern Tab Switcher */}
        <div className="p-6 pb-4">
          <div className="bg-gray-50 p-1 rounded-2xl border border-gray-200">
            <div className="grid grid-cols-3 gap-1">
              <button
                onClick={() => setEditorView('profile')}
                className={`relative py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 min-w-[120px] ${
                  editorView === 'profile'
                    ? 'bg-white text-emerald-600 shadow-lg shadow-emerald-500/20'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <User className="w-4 h-4" />
                  <span className="text-xs">Profile</span>
                </div>
                {editorView === 'profile' && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-emerald-500 rounded-full"></div>
                )}
              </button>

              <button
                onClick={() => setEditorView('content')}
                className={`relative py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 min-w-[120px] ${
                  editorView === 'content'
                    ? 'bg-white text-blue-600 shadow-lg shadow-blue-500/20'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <PenSquare className="w-4 h-4" />
                  <span className="text-xs">Content</span>
                </div>
                {editorView === 'content' && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-blue-500 rounded-full"></div>
                )}
              </button>

              <button
                onClick={() => setEditorView('design')}
                className={`relative py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-300 min-w-[120px] ${
                  editorView === 'design'
                    ? 'bg-white text-purple-600 shadow-lg shadow-purple-500/20'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <LayoutTemplate className="w-4 h-4" />
                  <span className="text-xs">Design</span>
                </div>
                {editorView === 'design' && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-purple-500 rounded-full"></div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Editor Area */}
        <div className="flex-grow overflow-y-scroll custom-scrollbar scroll-smooth">
          {editorView === 'profile' && (
            <div className="px-4 py-6">
              {/* Import Resume Card */}
              <div className="mb-5">
                <div
                  onClick={handleImportClick}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 p-6 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 text-center text-white">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-xl mb-3 group-hover:bg-white/30 transition-colors duration-300">
                      <Upload className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">Import Your Resume</h3>
                    <p className="text-blue-100 mb-3 text-xs">Upload PDF, DOCX, DOC, or TXT (up to 10MB)</p>
                    <div className={`inline-flex items-center px-5 py-2.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 font-semibold text-sm transition-all duration-300 ${
                      isUploading ? 'cursor-not-allowed opacity-75' : 'hover:bg-white/30'
                    }`}>
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Choose File
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleResumeUpload}
                  className="hidden"
                />

                {/* Upload Status */}
                {uploadMessage && (
                  <div className={`mt-3 p-3 rounded-lg border-l-4 ${
                    uploadMessage.includes('successfully')
                      ? 'bg-emerald-50 border-emerald-400 text-emerald-800'
                      : uploadMessage.includes('error') || uploadMessage.includes('Failed')
                        ? 'bg-red-50 border-red-400 text-red-800'
                        : 'bg-blue-50 border-blue-400 text-blue-800'
                  }`}>
                    <div className="flex items-center">
                      {uploadMessage.includes('successfully') ? (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 mr-2" />
                      )}
                      <span className="text-sm font-medium">{uploadMessage}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                <button
                  onClick={() => setEditorView('content')}
                  className="group relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 hover:shadow-md transition-all duration-300 hover:from-blue-100 hover:to-indigo-100"
                >
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-500 rounded-lg mb-2 text-white group-hover:scale-110 transition-transform duration-300">
                      <PenSquare className="w-5 h-5" />
                    </div>
                    <h3 className="font-semibold text-blue-900 text-sm mb-1">Edit Content</h3>
                    <p className="text-xs text-blue-700">Craft your story</p>
                  </div>
                </button>

                <button
                  onClick={() => setEditorView('design')}
                  className="group relative overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 hover:shadow-md transition-all duration-300 hover:from-purple-100 hover:to-pink-100"
                >
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-500 rounded-lg mb-2 text-white group-hover:scale-110 transition-transform duration-300">
                      <LayoutTemplate className="w-5 h-5" />
                    </div>
                    <h3 className="font-semibold text-purple-900 text-sm mb-1">Choose Design</h3>
                    <p className="text-xs text-purple-700">Select templates</p>
                  </div>
                </button>
              </div>

              {/* Statistics Card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Resume Overview</h3>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Work Experience', value: resumeData.experience?.length || 0, color: 'blue' },
                    { label: 'Education', value: resumeData.education?.length || 0, color: 'purple' },
                    { label: 'Skills', value: resumeData.skills?.reduce((acc, cat) => acc + cat.skills.length, 0) || 0, color: 'green' },
                    { label: 'Projects', value: resumeData.projects?.length || 0, color: 'orange' }
                  ].map(({ label, value, color }) => (
                    <div key={label} className="text-center">
                      <div className={`text-xl font-bold ${
                        color === 'blue' ? 'text-blue-600' :
                        color === 'purple' ? 'text-purple-600' :
                        color === 'green' ? 'text-green-600' :
                        'text-orange-600'
                      }`}>
                        {value}
                      </div>
                      <div className="text-xs text-gray-500 font-medium">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {editorView === 'content' && (
            <div className="px-4 py-6">
              <ResumeEditor
                data={resumeData}
                onChange={setResumeData}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>
          )}
          
          {editorView === 'design' && (
            <div className="px-4 py-6">
              <div className="mb-5">
                <h2 className="text-lg font-bold text-gray-800 mb-1">Choose Your Style</h2>
                <p className="text-xs text-gray-600">Select a template that matches your profession</p>
              </div>

              <div className="space-y-5">
                {/* Template Selection */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-4">Resume Templates ({Object.keys(TEMPLATE_REGISTRY).length} Available)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
                    {Object.values(TEMPLATE_REGISTRY).map((template) => (
                      <div key={template.id} className="relative group">
                        <button
                          onClick={() => setSelectedTemplate(template.id)}
                          className={`w-full p-4 border rounded-xl text-left transition-all duration-300 hover:shadow-md ${
                            selectedTemplate === template.id
                              ? template.preview.accentClass + ' ring-2 ring-offset-1 ring-current shadow-md'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          {template.popular && (
                            <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-md">
                              Popular
                            </div>
                          )}

                          <div className={`w-full h-24 rounded-lg mb-3 bg-gradient-to-br ${template.preview.gradientClass} shadow-inner relative overflow-hidden`}>
                            <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                            <div className="absolute top-2 left-2 right-2">
                              <div className="h-1.5 bg-white/30 rounded mb-1.5"></div>
                              <div className="h-1 bg-white/20 rounded w-3/4 mb-1"></div>
                              <div className="h-1 bg-white/20 rounded w-1/2"></div>
                            </div>
                            <div className="absolute bottom-2 left-2 right-2 space-y-0.5">
                              <div className="h-0.5 bg-white/25 rounded"></div>
                              <div className="h-0.5 bg-white/25 rounded w-4/5"></div>
                              <div className="h-0.5 bg-white/25 rounded w-3/5"></div>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <h4 className="font-bold text-gray-800 text-sm">{template.name}</h4>
                            <p className="text-xs text-gray-600 leading-relaxed">{template.description}</p>
                          </div>

                          {selectedTemplate === template.id && (
                            <div className="mt-2 flex items-center text-xs font-medium text-current">
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                              Selected
                            </div>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Color Themes */}
                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Color Themes (Pastel)</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { name: 'blue', colors: ['bg-blue-100', 'bg-blue-200', 'bg-blue-300'] },
                      { name: 'purple', colors: ['bg-purple-100', 'bg-purple-200', 'bg-purple-300'] },
                      { name: 'green', colors: ['bg-green-100', 'bg-green-200', 'bg-green-300'] },
                      { name: 'pink', colors: ['bg-pink-100', 'bg-pink-200', 'bg-pink-300'] },
                      { name: 'amber', colors: ['bg-amber-100', 'bg-amber-200', 'bg-amber-300'] },
                      { name: 'teal', colors: ['bg-teal-100', 'bg-teal-200', 'bg-teal-300'] }
                    ].map((theme) => (
                      <button
                        key={theme.name}
                        onClick={() => setSelectedColor(theme.name)}
                        className={`group relative p-3 rounded-xl border-2 transition-all duration-300 ${
                          selectedColor === theme.name
                            ? 'border-blue-500 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex justify-center space-x-1 mb-2">
                          {theme.colors.map((color, index) => (
                            <div key={index} className={`w-4 h-4 rounded-full ${color} shadow-sm`}></div>
                          ))}
                        </div>
                        <p className="text-xs font-semibold text-gray-700 capitalize text-center">{theme.name}</p>
                        {selectedColor === theme.name && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    Selected: <span className="font-semibold capitalize">{selectedColor}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

      </aside>

      {/* Right Panel - Live Preview */}
      <main className="flex-1 flex flex-col">
        {/* Preview Header */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-black p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Live Preview</h2>
              <p className="text-gray-300 text-sm">Real-time resume rendering</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="group flex items-center space-x-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 text-white px-5 py-2.5 rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>

            {/* Language Selector */}
            <div className="relative">
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                disabled={isTranslating}
                className="appearance-none bg-gray-800 border border-gray-600 text-white pl-4 pr-10 py-3 text-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code} className="bg-gray-800">
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
              {isTranslating ? (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 animate-spin" />
              ) : (
                <Globe className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              )}
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center bg-gray-800 border border-gray-600 rounded-xl p-1">
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300 hover:text-white"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-300 px-3 min-w-[60px] text-center font-medium">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300 hover:text-white"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={handleZoomReset}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300 hover:text-white"
                title="Reset Zoom"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Resume Canvas */}
        <div className="flex-1 overflow-auto p-8">
          <div className="flex justify-center">
            <div
              id="resume-preview-wrapper"
              style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top center', transition: 'transform 0.2s' }}
            >
              <ResumePreview
                data={displayData}
                template={selectedTemplate}
                language={selectedLanguage}
                color={selectedColor}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Template Gallery Modal (can be removed if TemplateSelector is preferred) */}
      {showTemplateGallery && (
        <TemplateGallery
          selectedTemplate={selectedTemplate}
          onSelect={setSelectedTemplate}
          onClose={() => setShowTemplateGallery(false)}
        />
      )}
    </div>
  )
}
